/**
 * デュエルのページの試合ごとの「修正の会話」（Issue #947。索引 #944）。入力の間違いを文で伝えると、サーバーの Claude が記録の差分を考え、
 * 後ろの手への波及まで試して提案する（#946）。ここは描くだけ: 発言を送る・考えている間はポーリング・提案を見せて「当てる」「取り下げる」・当てたら「元に戻す」。
 * 開くまでは何も読まない（ページの試合の数だけ呼ばない）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fixApply, fixCancel, fixReplayId, fixReset, fixSend, fixState, revertPatch } from './ocgCore';

export const POLL_MS = 2000;

// 提案の手ごとの区分（サーバーの ocg_fix.NOTE_KINDS）
const NOTE_KIND = {
  follow: { label: '追従', color: '#9a9', bg: 'transparent' },
  guess: { label: '推測', color: '#fc6', bg: 'rgba(255, 204, 102, 0.08)' },
  conflict: { label: '矛盾', color: '#f88', bg: 'rgba(255, 136, 136, 0.1)' },
};

const ROLE_STYLE = {
  user: { background: '#1d2a3b', color: '#dde', alignSelf: 'flex-end' },
  assistant: { background: '#1b1b1b', color: '#ddd' },
  tool: { color: '#777', fontSize: '0.75rem', padding: '0 8px' },
  proposal: { color: '#9cf', fontSize: '0.75rem', padding: '0 8px' },
  info: { color: '#8a8', fontSize: '0.75rem', padding: '0 8px' },
  error: { color: '#f99', fontSize: '0.8rem', padding: '0 8px' },
};

/** 発言を id で重ねずにつなぐ */
export function mergeMessages(prev, more) {
  const seen = new Set(prev.map((m) => m.id));
  return [...prev, ...(more ?? []).filter((m) => !seen.has(m.id))];
}

function Message({ m }) {
  const style = ROLE_STYLE[m.role] ?? ROLE_STYLE.assistant;
  const bubble = m.role === 'user' || m.role === 'assistant';
  return (
    <div data-testid={`fix-msg-${m.role}`} style={{
      whiteSpace: 'pre-wrap', maxWidth: bubble ? '92%' : '100%', borderRadius: 6,
      padding: bubble ? '6px 10px' : undefined, fontSize: '0.85rem', ...style,
    }}>{m.role === 'tool' ? `・${m.text}` : m.text}</div>
  );
}

/** 再現ログの差分の提案（演出のタイミングの提案＝シミュレータの操作の時刻を動かすもの は Issue #998 で旧エンジンと一緒に消した） */
export function Proposal({ proposal, stale, busy, onApply, onDiscard }) {
  const [showFollow, setShowFollow] = useState(false);
  const notes = proposal.notes ?? [];
  const follows = notes.filter((n) => n.kind === 'follow');
  const shown = notes.filter((n) => n.kind !== 'follow' || showFollow);
  const report = proposal.report ?? {};
  const problems = report.problems ?? [];
  const first = report.first_difference;
  return (
    <div data-testid="fix-proposal" style={{ border: '1px solid #345', borderRadius: 6, padding: 10, marginTop: 8 }}>
      <div style={{ fontWeight: 'bold', color: '#9cf', marginBottom: 6 }}>提案</div>
      <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', marginBottom: 8 }}>{proposal.summary}</div>
      {first ? (
        <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: 6 }}>
          最初に変わる所: T{first.turn}　{first.before ?? '（なし）'} → {first.after ?? '（なし）'}
        </div>
      ) : null}
      {problems.length ? (
        <ul data-testid="fix-problems" style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: '0.8rem', color: '#fc6' }}>
          {problems.map((p, i) => <li key={i}>{p.text}</li>)}
        </ul>
      ) : <div style={{ fontSize: '0.8rem', color: '#8a8', marginBottom: 8 }}>試合の結果（LP・決着）は変わりません</div>}
      <div style={{ fontSize: '0.8rem' }}>
        {shown.map((n, i) => {
          const k = NOTE_KIND[n.kind] ?? NOTE_KIND.follow;
          return (
            <div key={i} data-testid={`fix-note-${n.kind}`} style={{ background: k.bg, padding: '2px 6px', borderRadius: 4 }}>
              <span style={{ color: k.color, marginRight: 6 }}>{k.label}</span>
              <span style={{ color: '#aaa', marginRight: 6 }}>#{n.seq}</span>
              <span>{n.reason}</span>
            </div>
          );
        })}
        {follows.length && !showFollow ? (
          <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 4 }} onClick={() => setShowFollow(true)}>
            追従した手 {follows.length} 手も見る
          </button>
        ) : null}
      </div>
      {stale ? (
        <div role="alert" style={{ color: '#f99', fontSize: '0.8rem', marginTop: 8 }}>
          提案したあとに記録が変わっています。当てられないので、もう一度試すよう頼んでください
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-sm btn-primary" data-testid="fix-apply" disabled={busy || stale} onClick={onApply}>当てる</button>
        <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={onDiscard}>取り下げる</button>
      </div>
    </div>
  );
}

export default function FixChatPanel({ gameUuid }) {
  const [open, setOpen] = useState(false);
  const [replayId, setReplayId] = useState(undefined);   // undefined = まだ引いていない / null = replay が無い
  const [state, setState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const lastId = useRef(0);
  const listRef = useRef(null);

  const take = useCallback((st, { replace = false } = {}) => {
    setState(st);
    setMessages((prev) => {
      const next = replace ? (st.messages ?? []) : mergeMessages(prev, st.messages);
      lastId.current = next.length ? next[next.length - 1].id : 0;
      return next;
    });
  }, []);

  // 開いたら replay を引いて最初の状態
  useEffect(() => {
    if (!open || replayId !== undefined) return;
    let alive = true;
    (async () => {
      try {
        const id = await fixReplayId(gameUuid);
        if (!alive) return;
        setReplayId(id);
        if (id != null) take(await fixState(id, 0), { replace: true });
      } catch (e) {
        if (alive) setError(String(e?.message ?? e));
      }
    })();
    return () => { alive = false; };
  }, [open, replayId, gameUuid, take]);

  // 考えている間はポーリング
  const running = state?.status === 'running';
  useEffect(() => {
    if (!running || replayId == null) return undefined;
    const t = setTimeout(async () => {
      try { take(await fixState(replayId, lastId.current)); } catch (e) { setError(String(e?.message ?? e)); }
    }, POLL_MS);
    return () => clearTimeout(t);
  }, [running, replayId, state, take]);

  useEffect(() => {
    const el = listRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [messages.length]);

  const act = async (fn, { replace = false } = {}) => {
    setBusy(true); setError(null);
    try {
      const st = await fn();
      if (st) take(st, { replace });
      return st ?? true;
    } catch (e) {
      setError(String(e?.message ?? e));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    if (await act(() => fixSend(replayId, t))) setText('');
  };

  if (!open) {
    return (
      <div style={{ marginTop: 8 }}>
        <button type="button" className="btn btn-sm btn-secondary" data-testid="fix-open" onClick={() => setOpen(true)}
          title="入力の間違いを文で伝えると、AI が記録の直し方を考えて提案します（Issue #947）">修正の会話</button>
      </div>
    );
  }
  return (
    <div data-testid="fix-chat" style={{ marginTop: 10, border: '1px solid #262626', borderRadius: 8, padding: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 'bold', color: '#aaa' }}>修正の会話</span>
        {state ? (
          <span style={{ fontSize: '0.75rem', color: '#777' }} title="この試合の修正の会話で使った金額（上限）">
            ${Number(state.spent_usd ?? 0).toFixed(2)} / ${Number(state.budget_usd ?? 0).toFixed(0)}
          </span>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {state?.applied_patch_id && !state?.proposal ? (
            <button type="button" className="btn btn-sm btn-secondary" data-testid="fix-revert" disabled={busy || running}
              title="最後に当てた提案の前の記録に戻す（当てたあとに記録を変えていなければ）"
              onClick={() => act(async () => { await revertPatch(replayId, state.applied_patch_id); return fixState(replayId, lastId.current); })}>
              当てた提案を元に戻す
            </button>
          ) : null}
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy || running || !messages.length}
            onClick={() => act(() => fixReset(replayId), { replace: true })}>会話を消す</button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setOpen(false)}>閉じる</button>
        </div>
      </div>
      {replayId === null ? (
        <p style={{ color: '#777', fontSize: '0.85rem' }}>この試合には再現エンジンの入力がありません</p>
      ) : (
        <>
          <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 360, overflowY: 'auto', marginBottom: 8 }}>
            {messages.length === 0 && !running ? (
              <p style={{ color: '#666', fontSize: '0.8rem', margin: 0 }}>
                直したいことを書いてください（例: 5 ターン目のビッグ・シールド・ガードナーはセットではなく攻撃表示で召喚）
              </p>
            ) : null}
            {messages.map((m) => <Message key={m.id} m={m} />)}
            {running ? (
              <div data-testid="fix-running" style={{ color: '#888', fontSize: '0.8rem' }}>
                考えています…{state?.cancel_requested ? '（止めています）' : ''}
              </div>
            ) : null}
          </div>
          {state?.proposal ? (
            <Proposal proposal={state.proposal} stale={state.proposal_stale} busy={busy || running}
              onApply={() => act(() => fixApply(replayId))}
              onDiscard={() => act(() => fixCancel(replayId, { discardProposal: true }))} />
          ) : null}
          <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'flex-end' }}>
            <textarea className="form-input" data-testid="fix-text" rows={2} value={text} disabled={running}
              placeholder={running ? '考えています…' : '直したいこと・聞かれたことへの答え'}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
              style={{ flex: 1, resize: 'vertical', fontSize: '0.85rem' }} />
            {running ? (
              <button type="button" className="btn btn-sm btn-secondary" disabled={state?.cancel_requested}
                onClick={() => act(() => fixCancel(replayId))}>止める</button>
            ) : (
              <button type="button" className="btn btn-sm btn-primary" data-testid="fix-send" disabled={busy || !text.trim()} onClick={send}>送る</button>
            )}
          </div>
        </>
      )}
      {error ? <div role="alert" style={{ color: '#f99', fontSize: '0.8rem', marginTop: 6 }}>{error}</div> : null}
    </div>
  );
}
